import React, { useState, useEffect } from "react";
import { ArrowLeft, Loader } from "lucide-react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "utils/constant";
import PDFReport from "./PDFReport";
import PermissionEmptyState from "components/UI_components/EmptyStateFinance";
import { rtdb } from "utils/firebase";
import { environment } from "utils/constant";
import {
  MdCalendarToday,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdFileDownload,
  MdLocalShipping,
  MdOutlineAccountBalanceWallet,
  MdOutlinePictureAsPdf,
  MdOutlineTask,
  MdRefresh,
} from "react-icons/md";
import { MdOutlineGroup } from "react-icons/md";
import DeliveryIcon from "assets/Deliveries.svg";
import SyncIcon from "assets/Change.svg";
import { getFinanceCategories } from "utils/getFinanceCategories";
const ReportGenerator = ({ userPermissions, permissionsLoading }) => {
  const navigate = useNavigate();
  const categoryData = getFinanceCategories();
  const refundCategory = categoryData.refundCategory;
  const receivedCategory = categoryData.receivedCategory;
  const [selectedSections, setSelectedSections] = useState({
    site_updates: true,
    payments: true,
    attendance: true,
    material_inventory: true,
    material_deliveries: true,
    tasks: true,
  });

  const [dateRange, setDateRange] = useState({
    start_date: new Date().toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
  });

  const [selectedTimePeriod, setSelectedTimePeriod] = useState("Today");

  const siteData = JSON.parse(localStorage.getItem("siteData") || "{}");
  const organization = JSON.parse(localStorage.getItem("organization") || "{}");
  const organizationLogo = organization?.logo_image_file || null;
  const siteId = localStorage.getItem("site");

  const [exportFormat, setExportFormat] = useState("PDF");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [configChanged, setConfigChanged] = useState(false);

  const sections = [
    {
      id: "site_updates",
      label: "Site Updates",
      description: "See site updates and photos at your project",
      icon: (
        <img
          src={SyncIcon}
          alt="Site Updates"
          style={{ width: 24, height: 24 }}
        />
      ),
    },
    {
      id: "payments",
      label: "Finance",
      description: "Manage payment & transaction for your report",
      icon: <MdOutlineAccountBalanceWallet size={28} color="#B50000B0" />,
    },
    {
      id: "attendance",
      label: "Attendance",
      description: "View attendance of your report across project",
      icon: <MdOutlineGroup size={24} color="#CA7900AB" />,
    },
    {
      id: "material_inventory",
      label: "Material Inventory",
      description: "See what materials you have in your inventory",
      icon: <MdLocalShipping size={24} color="#6A02BC8A" />,
    },
    {
      id: "material_deliveries",
      label: "Material Deliveries",
      description: "Manage deliveries in your material report",
      icon: (
        <img
          src={DeliveryIcon}
          alt="Material Deliveries"
          style={{ width: 24, height: 24 }}
        />
      ),
    },
    {
      id: "tasks",
      label: "Tasks",
      description: "View and manage tasks for your project",
      icon: <MdOutlineTask size={24} color="#0094FFC2" />,
    },
  ];

  useEffect(() => {
    if (configChanged) {
      setReportData(null);
      setIsDownloading(false);
      setConfigChanged(false);
    }
  }, [configChanged]);

  const handleDateChange = (field, value) => {
    const today = new Date().toISOString().split("T")[0];
    if (value > today) return;

    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
    setSelectedTimePeriod("Custom");
    setConfigChanged(true);
  };

  const handleTimePeriodChange = (period) => {
    setSelectedTimePeriod(period);
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case "Today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "7 Days":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        endDate = new Date(today);
        break;
      case "1 Month":
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        endDate = new Date(today);
        break;
      case "Last Month":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    setDateRange({
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    });
    setConfigChanged(true);
  };

  const getImageUrl = async (mediaId) => {
    if (!mediaId) return null;
    try {
      const accessToken = localStorage.getItem("access");
      const response = await fetch(`${BASE_URL}/core/api/snag/${mediaId}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        return data.data.image;
      }
      return null;
    } catch (error) {
      console.error("Error fetching media URL:", error);
      return null;
    }
  };

  const getDateTimestamp = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.getTime();
    } catch (error) {
      return null;
    }
  };

  const normalizeTimestamp = (timestamp) => {
    if (!timestamp) return null;
    if (typeof timestamp === "number") return timestamp;
    if (typeof timestamp === "string") {
      const parsed = new Date(timestamp).getTime();
      return isNaN(parsed) ? null : parsed;
    }
    if (timestamp instanceof Date) return timestamp.getTime();
    return null;
  };

  const fetchSiteUpdates = async () => {
    if (
      !selectedSections.site_updates ||
      !siteId ||
      !dateRange.start_date ||
      !dateRange.end_date
    ) {
      return {};
    }

    const generalMessagesRef = rtdb.ref(
      `site_snags-${environment}/${siteId}/general_updates/messages`
    );

    const startTimestamp = getDateTimestamp(dateRange.start_date);
    const endTimestamp = getDateTimestamp(dateRange.end_date);
    if (!startTimestamp || !endTimestamp) return {};

    const endOfDayTimestamp = endTimestamp + 24 * 60 * 60 * 1000 - 1;

    try {
      const snapshot = await generalMessagesRef.once("value");
      if (!snapshot.exists()) return {};

      const allMessages = [];
      const newImageUrls = {};

      snapshot.forEach((childSnapshot) => {
        const messageData = childSnapshot.val();
        if (!messageData) return;

        const messageTimestamp = normalizeTimestamp(messageData.createdAt);
        if (!messageTimestamp) return;

        if (
          messageTimestamp >= startTimestamp &&
          messageTimestamp <= endOfDayTimestamp
        ) {
          allMessages.push({
            id: childSnapshot.key,
            ...messageData,
            createdAt: messageTimestamp,
          });
        }
      });

      const imagePromises = allMessages
        .filter((msg) => msg.has_media)
        .map(async (msg) => {
          if (
            !Array.isArray(msg.media) &&
            msg.media.type === "image" &&
            !newImageUrls[msg.media.mediaId]
          ) {
            const url = await getImageUrl(msg.media.mediaId);
            console.log("Old Image URL:", msg.media.mediaId, url);
            if (url) newImageUrls[msg.media.mediaId] = url;
          } else if (Array.isArray(msg.media)) {
            for (const media of msg.media) {
              if (!newImageUrls[media.mediaId] && media.type === "image") {
                const url = await getImageUrl(media.mediaId);
                console.log("New Image URL:", media.mediaId, url);
                if (url) newImageUrls[media.mediaId] = url;
              }
            }
          }
        });

      await Promise.all(imagePromises);

      const groupedUpdates = allMessages.reduce((acc, msg) => {
        const date = new Date(msg.createdAt).toLocaleDateString();
        if (!acc[date]) acc[date] = [];
        acc[date].push({
          comment: msg.text || "No comment",
          media:
            Array.isArray(msg.media) && msg.has_media
              ? msg.media.map((media) => ({
                  mediaId: media.mediaId,
                  url:
                    newImageUrls[media.mediaId] ||
                    "https://cdn-icons-png.flaticon.com/512/13434/13434972.png",
                  // caption:only time in hh:mm:ss format
                  caption: new Date(msg.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                }))
              : msg.has_media &&
                !Array.isArray(msg.media) &&
                msg.media.type === "image"
              ? [
                  {
                    mediaId: msg.media.mediaId,
                    url:
                      newImageUrls[msg.media.mediaId] ||
                      "https://cdn-icons-png.flaticon.com/512/13434/13434972.png",
                    caption: new Date(msg.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                  },
                ]
              : [],

          createdAt: msg.createdAt,
          senderName: msg.senderName || "Unknown",
        });
        return acc;
      }, {});

      Object.keys(groupedUpdates).forEach((date) => {
        groupedUpdates[date].sort((a, b) => a.createdAt - b.createdAt);
      });

      return groupedUpdates;
    } catch (error) {
      console.error("Error fetching site updates:", error);
      return {};
    }
  };

  const fetchReportData = async () => {
    setIsLoading(true);
    setError(null);
    console.log("Fetching report data with sections:", dateRange);

    try {
      const sites = localStorage.getItem("site");
      const token = localStorage.getItem("access");

      const [apiResponse, siteUpdates] = await Promise.all([
        axios.post(
          `${BASE_URL}/task/generate-report/`,
          {
            sites: parseInt(sites),
            ...selectedSections,
            ...dateRange,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        fetchSiteUpdates(),
      ]);

      const combinedData = {
        ...apiResponse.data,
        data: {
          ...apiResponse.data.data,
          site_updates: siteUpdates,
        },
      };
      console.log("Combined Report Data:", combinedData);
      setReportData(combinedData);
      return combinedData;
    } catch (err) {
      setError("Failed to generate report. Please try again.");
      console.error("Report generation error:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const generateExcel = async (data) => {
    try {
      setIsDownloading(true);

      const wb = XLSX.utils.book_new();
      const siteInfoSheet = [
        {
          "Site Name": siteData.site_name,
          Address: siteData.address,
          City: siteData.city,
          State: siteData.state,
          Pincode: siteData.pincode,
        },
      ];
      const wsInfo = XLSX.utils.json_to_sheet(siteInfoSheet);
      XLSX.utils.book_append_sheet(wb, wsInfo, "Site Information");

      Object.entries(selectedSections).forEach(([section, isSelected]) => {
        if (isSelected && data[section]) {
          const formattedData = formatDataForExcel(data[section], section);
          const ws = XLSX.utils.json_to_sheet(formattedData);
          const maxWidth = 50;
          const columnWidths = {};
          formattedData.forEach((row) => {
            Object.keys(row).forEach((key) => {
              const length = String(row[key]).length;
              columnWidths[key] = Math.min(
                maxWidth,
                Math.max(columnWidths[key] || 0, length)
              );
            });
          });
          ws["!cols"] = Object.keys(columnWidths).map((key) => ({
            wch: columnWidths[key],
          }));
          XLSX.utils.book_append_sheet(
            wb,
            ws,
            section.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
          );
        }
      });

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `project_report_${dateRange.start_date}.xlsx`);
    } catch (err) {
      setError("Failed to generate Excel file. Please try again.");
      console.error("Excel generation error:", err);
    } finally {
      setTimeout(() => setIsDownloading(false), 2000);
    }
  };

  const handleGenerateReport = async () => {
    const data = await fetchReportData();
    if (!data) return;

    if (exportFormat === "Excel") {
      await generateExcel(data.data);
    }
  };

  const SectionCard = ({ section }) => (
    <button
      type="button"
      onClick={() => {
        setSelectedSections((prev) => ({
          ...prev,
          [section.id]: !prev[section.id],
        }));
        setConfigChanged(true);
      }}
      className={`rounded-lg border px-3 py-2 text-left transition ${
        selectedSections[section.id]
          ? "border-[#3B0069] bg-[#F7F2FF]"
          : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        {selectedSections[section.id] ? (
          <MdCheckBox className="mt-0.5 shrink-0 text-[#3B0069]" size={18} />
        ) : (
          <MdCheckBoxOutlineBlank
            className="mt-0.5 shrink-0 text-slate-300"
            size={18}
          />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {/* <span className="shrink-0">{section.icon}</span> */}
            <p
              className={`text-sm font-semibold ${
                selectedSections[section.id] ? "text-[#3B0069]" : "text-slate-800"
              } ${section.id === "payments" ? "ml-1" : ""}`}
            >
              {section.label}
            </p>
          </div>
          {/* {section.description && (
            <p className="mt-1 text-xs leading-5 text-slate-500">
              {section.description}
            </p>
          )} */}
        </div>
      </div>
    </button>
  );

  const formatDataForExcel = (data, section) => {
    switch (section) {
      case "site_updates":
        return Object.entries(data).flatMap(([date, updates]) =>
          updates.map((update) => ({
            Date: date,
            Time: new Date(update.createdAt).toLocaleTimeString(),
            Comment: update.comment,
            "Posted By": update.senderName,
            "Has Photo": update.mediaUrl ? "Yes" : "No",
          }))
        );

      case "payments":
        return data.map((payment) => ({
          Amount: `₹${parseFloat(payment.amount).toFixed(2)}`,
          Date: payment.transaction_date,
          Method: payment.transaction_method,
          Type: payment.transaction_type || payment?.payment_type || "N/A",
          Comments: payment.comments || "",
          Purpose:
            payment.transaction_catagory_id === refundCategory ||
            payment.transaction_catagory_id === receivedCategory
              ? "Credit"
              : "Debit",
        }));

      case "attendance":
        const attendanceRows = [];
        Object.entries(data).forEach(([date, attendance]) => {
          if (attendance.total_present > 0) {
            attendanceRows.push({
              Date: date,
              "Total Present": attendance.total_present,
              "Worker Present": attendance.worker_present,
              "Worker Absent": attendance.worker_absent,
              "Worker Name": "",
              "Work Type": "",
              Attendance: "",
            });
            attendance.data.forEach((record) => {
              attendanceRows.push({
                Date: date,
                "Total Present": "",
                "Worker Present": "",
                "Worker Absent": "",
                "Worker Name": record.worker_name,
                "Work Type": record.worker_workname,
                Attendance: record.attendance,
              });
            });
          }
        });
        return attendanceRows;

      case "material_inventory":
        const inventoryRows = [];
        Object.entries(data).forEach(([category, items]) => {
          inventoryRows.push({
            Category: category,
            "Current Quantity": "",
            "Update Date": "",
            "Entered Quantity": "",
            Comment: "",
          });
          items.forEach((item) => {
            inventoryRows.push({
              Category: category,
              "Current Quantity": item.current_quantity,
              "Update Date": item.update_date,
              "Entered Quantity": item.entered_quantity,
              Comment: item.comment,
            });
          });
        });
        return inventoryRows;

      case "tasks":
        const taskRows = [];
        data.forEach((task) => {
          taskRows.push({
            "Task Name": task.task_name,
            Status: task.task_status,
            Details: task.task_details,
            Priority: task.priority,
            Timeline: `${task.start_date} to ${task.end_date}`,
            "Estimated Amount": `₹${parseFloat(task.estimated_amount).toFixed(
              2
            )}`,
            Type: "Main Task",
            "Subtask Name": "",
            Material: "",
            Quantity: "",
            UOM: "",
          });
          if (task.subtasks?.length > 0) {
            task.subtasks.forEach((subtask) => {
              taskRows.push({
                "Task Name": task.task_name,
                Status: subtask.is_completed ? "Completed" : "Pending",
                Details: "",
                Priority: "",
                Timeline: `${subtask.start_date} to ${subtask.end_date}`,
                "Estimated Amount": "",
                Type: "Subtask",
                "Subtask Name": subtask.subtask_name,
                Material: "",
                Quantity: "",
                UOM: "",
              });
            });
          }
          if (task.task_materials?.length > 0) {
            task.task_materials.forEach((material) => {
              taskRows.push({
                "Task Name": task.task_name,
                Status: "",
                Details: "",
                Priority: "",
                Timeline: "",
                "Estimated Amount": "",
                Type: "Material",
                "Subtask Name": "",
                Material: material.material_name,
                Quantity: `${material.estimated_quantity}/${material.total_used}`,
                UOM: material.uom,
              });
            });
          }
        });
        return taskRows;

      default:
        return data;
    }
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-10 h-10 animate-spin text-purple-500" />
      </div>
    );
  }

  if (
    !userPermissions.is_admin &&
    !userPermissions.report_generation &&
    !permissionsLoading
  ) {
    return (
      <PermissionEmptyState
        module="Report Generation"
        description="You don't have permission to access the report generation for this site. Only the administrator can access this feature."
      />
    );
  }

  const handlePDFDownload = (e) => {
    if (isDownloading) {
      e.preventDefault();
      return false;
    }
    setIsDownloading(true);
    setTimeout(() => setIsDownloading(false), 3000);
  };

  const todayDate = new Date().toISOString().split("T")[0];
  const selectedSectionIds = Object.entries(selectedSections)
    .filter(([, isSelected]) => isSelected)
    .map(([sectionId]) => sectionId);
  const selectedSectionCount = selectedSectionIds.length;
  const hasGeneratedReport = Boolean(reportData);
  const previewDocumentData = reportData || { data: {} };
  const previewDocumentKey = [
    hasGeneratedReport ? "generated" : "preview",
    dateRange.start_date,
    dateRange.end_date,
    selectedSectionIds.join(","),
  ].join("|");
  const fileName = `${String(siteData.site_name || "project")
    .replace(/\s+/g, "_")
    .toLowerCase()}_report_${Date.now()}.pdf`;
  const isGenerateDisabled =
    isLoading || isDownloading || selectedSectionCount === 0;
  const generateHint = (() => {
    if (error) return error;
    if (selectedSectionCount === 0) return "Select at least one section.";
    return hasGeneratedReport
      ? "Live PDF is ready to download."
      : "Generate once to replace the preview with live report data.";
  })();

  const setAllSections = (isSelected) => {
    setSelectedSections(
      sections.reduce(
        (acc, section) => ({
          ...acc,
          [section.id]: isSelected,
        }),
        {}
      )
    );
    setConfigChanged(true);
  };

  const resetFilters = () => {
    setDateRange({
      start_date: todayDate,
      end_date: todayDate,
    });
    setSelectedTimePeriod("Today");
    setAllSections(true);
    setExportFormat("PDF");
    setReportData(null);
    setError(null);
    setIsDownloading(false);
  };

  return (
    <div
      className="flex-1 h-[91vh] overflow-hidden bg-gray-50"
      style={{ scrollbarWidth: "none" }}
    >
      <div className="px-10 py-8">
        <div className="mb-8 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-1 rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Generate Report
              </h1>
              <p className="text-sm text-muted-foreground">
                Generate a comprehensive report for this project
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-lg border border-white/70 bg-white/85 px-3 py-2 backdrop-blur">
              <p className="text-sm font-medium text-slate-900">
                {selectedSectionCount} sections
              </p>
            </div>
            <button
              onClick={handleGenerateReport}
              disabled={isGenerateDisabled}
              className={`inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isGenerateDisabled
                  ? "cursor-not-allowed bg-[#3B0069]/50 text-white/50"
                  : "bg-[#3B0069] text-white hover:-translate-y-0.5 hover:bg-[#3B0069]/80"
              }`}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-[#3B0069]/25 border-t-[#3B0069] animate-spin" />
                  Generating report...
                </span>
              ) : (
                "Generate PDF Preview"
              )}
            </button>
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <MdRefresh size={18} />
              Reset
            </button>
          </div>
        </div>

        <div className="grid items-start gap-6 xl:grid-cols-[480px,minmax(0,1fr)]">
          <div className="space-y-5">
            <section className="rounded-lg border h-[75vh] overflow-y-scroll border-white/70 bg-white/90 p-4 backdrop-blur sm:p-3">
              <div className="space-y-6">
                <div>
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <MdCalendarToday className="text-[#3B0069]" />
                    Date Range
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {["Today", "7 Days", "1 Month", "Last Month"].map(
                      (period) => (
                        <button
                          key={period}
                          type="button"
                          onClick={() => handleTimePeriodChange(period)}
                          className={`rounded-lg border px-3 py-2.5 text-xs font-semibold transition ${
                            selectedTimePeriod === period
                              ? "border-[#3B0069] bg-[#3B0069] text-white shadow-lg shadow-[#3B0069]/20"
                              : "border-slate-200 bg-slate-50 text-slate-600 hover:border-[#3B0069]/25 hover:bg-[#F7F2FF] hover:text-[#3B0069]"
                          }`}
                        >
                          {period}
                        </button>
                      )
                    )}
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={dateRange.start_date}
                        max={todayDate}
                        onChange={(e) =>
                          handleDateChange("start_date", e.target.value)
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#3B0069] focus:ring-4 focus:ring-[#3B0069]/10"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={dateRange.end_date}
                        max={todayDate}
                        onChange={(e) =>
                          handleDateChange("end_date", e.target.value)
                        }
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-[#3B0069] focus:ring-4 focus:ring-[#3B0069]/10"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">
                        Sections
                      </h3>
                      <p className="text-xs text-slate-500">
                        Pick the modules that should appear in the PDF.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-semibold">
                      <button
                        type="button"
                        onClick={() => setAllSections(true)}
                        className="text-[#3B0069] transition hover:text-[#2A004D]"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setAllSections(false)}
                        className="text-slate-500 transition hover:text-slate-700"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    {sections.map((section) => (
                      <SectionCard key={section.id} section={section} />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="h-[75vh]">
            <section className="rounded-lg border h-full border-white/70 bg-white/90 px-4 py-3 backdrop-blur sm:p-5">
              <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-[#3B0069]/10 p-2 text-[#3B0069]">
                    <MdOutlinePictureAsPdf size={22} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950">
                      PDF Preview
                    </h2>
                    {/* <p
                      className={`text-xs ${
                        error ? "text-rose-600" : "text-slate-500"
                      }`}
                    >
                      {generateHint}
                    </p> */}
                  </div>
                </div>

                {hasGeneratedReport ? (
                  <PDFDownloadLink
                    document={
                      <PDFReport
                        data={reportData}
                        dateRange={dateRange}
                        selectedSections={selectedSections}
                        logoSrc={organizationLogo}
                      />
                    }
                    fileName={fileName}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                      isDownloading
                        ? "pointer-events-none bg-slate-200 text-slate-500"
                        : "bg-[#3B0069] text-white hover:bg-[#3B0069]/80"
                    }`}
                    onClick={handlePDFDownload}
                  >
                    {({ loading }) => (
                      <>
                        <MdFileDownload size={18} />
                        {loading
                          ? "Preparing PDF..."
                          : isDownloading
                          ? "Download Started..."
                          : "Download PDF"}
                      </>
                    )}
                  </PDFDownloadLink>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-400"
                  >
                    <MdFileDownload size={18} />
                    Generate to download
                  </button>
                )}
              </div>

              <div className="h-[65vh] overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                <div className="relative h-full w-full bg-white">
                  {isLoading ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-950/35 backdrop-blur-sm">
                      <div className="rounded-lg border border-white/15 bg-slate-950/75 px-6 py-5 text-center text-white">
                        <div className="mx-auto mb-3 h-6 w-6 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                        <p className="text-sm font-semibold">
                          Building your live report
                        </p>
                        <p className="mt-1 text-xs text-slate-300">
                          The preview will update once the PDF is ready.
                        </p>
                      </div>
                    </div>
                  ) : null}

                  <PDFViewer
                    key={previewDocumentKey}
                    className="h-full w-full border-none"
                  >
                    <PDFReport
                      data={previewDocumentData}
                      dateRange={dateRange}
                      selectedSections={selectedSections}
                      logoSrc={organizationLogo}
                    />
                  </PDFViewer>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportGenerator;
